import React, { useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import {
  Box,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  InputAdornment,
  Alert,
  Snackbar
} from '@mui/material';
// @iconify
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import plusFill from '@iconify/icons-eva/plus-fill';
import settingsFill from '@iconify/icons-eva/settings-fill';
// utils
import { OCPI_TOKEN_TYPES } from 'src/utils/ocpi.constants';

const TokenActions = ({
  searchTerm,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  selectedCount,
  totalCount,
  onCreateToken,
  onImportTokens,
  onExportAll,
  onExportSelected,
  onActivateSelected,
  onDeactivateSelected,
  onDeleteSelected,
  onAdvancedFilters
}) => {
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          const result = onImportTokens(jsonData);
          
          if (result.success) {
            setNotification({
              open: true,
              message: `Successfully imported ${result.count} tokens`,
              severity: 'success'
            });
          } else {
            setNotification({
              open: true,
              message: `Import failed: ${result.error}`,
              severity: 'error'
            });
          }
        } catch (error) {
          setNotification({
            open: true,
            message: 'Invalid JSON file format',
            severity: 'error'
          });
        }
      };
      reader.readAsText(file);
    }
    // Clear the input value to allow re-importing the same file
    event.target.value = '';
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const statusOptions = ['All Status', 'Valid', 'Invalid'];
  const typeOptions = ['All Types', ...OCPI_TOKEN_TYPES];

  return (
    <>
      <Box sx={{ mb: 3 }}>
        {/* Header Actions */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Typography variant="h4" component="h1">
            OCPI Tokens
          </Typography>
          
          <Stack direction="row" spacing={1}>
            {/* Create Token Button */}
            <Button
              variant="contained"
              onClick={onCreateToken}
              startIcon={<Icon icon={plusFill} />}
              size="medium"
            >
              Create Token
            </Button>
          </Stack>
        </Stack>

        {/* Search and Filters */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: 2, width: '100%' }}
        >
          {/* Search */}
          <TextField
            placeholder="Quick search tokens..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={searchFill} width={20} height={20} />
                </InputAdornment>
              )
            }}
            sx={{ flex: 1, minWidth: 300 }}
          />

          {/* Type Filter */}
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>All Types</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => onTypeFilterChange(e.target.value)}
              label="All Types"
            >
              {typeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>All Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              label="All Status"
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Advanced Filters */}
          <Button
            variant="outlined"
            onClick={onAdvancedFilters}
            startIcon={<Icon icon={settingsFill} />}
            sx={{ 
              minWidth: 'auto',
              bgcolor: 'white',
              borderColor: 'grey.300',
              color: 'text.primary',
              '&:hover': {
                bgcolor: 'grey.50',
                borderColor: 'grey.400'
              }
            }}
          >
            Advanced Filters
          </Button>

          {/* Results Count */}
          <Box>
            <Chip
              label={`${totalCount} tokens`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Stack>
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

TokenActions.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  typeFilter: PropTypes.string.isRequired,
  onTypeFilterChange: PropTypes.func.isRequired,
  statusFilter: PropTypes.string.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  selectedCount: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onCreateToken: PropTypes.func.isRequired,
  onImportTokens: PropTypes.func.isRequired,
  onExportAll: PropTypes.func.isRequired,
  onExportSelected: PropTypes.func.isRequired,
  onActivateSelected: PropTypes.func.isRequired,
  onDeactivateSelected: PropTypes.func.isRequired,
  onDeleteSelected: PropTypes.func.isRequired,
  onAdvancedFilters: PropTypes.func.isRequired
};

export default TokenActions;
